import { uuid } from 'shared/crypto'

import { exposeToWindow } from '../utils/window-functions'

import { androidAdapter } from './android-adapter'
import { iosAdapter } from './ios-adapter'
import { NativeMessageType } from './native-message-type'
import {
  type MessageCallback,
  type NativeRequestPayload,
  type NativeRequests,
  type NativeResponsePayload
} from './native-request'

export const NATIVE_TIMEOUT_MILLISECONDS = 3000
export const NATIVE_TIMEOUT_RETRY = 3
export const NATIVE_TIMEOUT = Symbol('NATIVE_TIMEOUT')
export const NATIVE_REQUEST_COMPLETE = Symbol('NATIVE_REQUEST_COMPLETE')
const NO_NATIVE_PLATFORM_FOUND_MESSAGE = 'No native platform found.'

export enum NativeBridgeRejectEvent {
  NO_NATIVE_PLATFORM_FOUND = 'NO_NATIVE_PLATFORM_FOUND',
  TIMEOUT = 'TIMEOUT'
}

interface MessengerExtraConfiguration {
  skipResponse?: boolean
}

interface ResponseHandler {
  resolve: MessageCallback
  reject: MessageCallback
  retry: number
  payload: NativeRequestPayload
}

const postToNative = <
  Key extends keyof NativeRequests | string = string,
  Data = Key extends keyof NativeRequests ? NativeRequests[Key]['Request'] : unknown
>(
  payload: NativeRequestPayload<Key, Data>
): boolean => {
  if (iosAdapter.isAvailable()) {
    iosAdapter.send(payload)
    return true
  }
  if (androidAdapter.isAvailable()) {
    androidAdapter.send(payload)
    return true
  }
  return false
}

const nativeAppResponseHandlers = new Map<
  string,
  ResponseHandler | typeof NATIVE_REQUEST_COMPLETE | typeof NATIVE_TIMEOUT
>()

const errorEvents = new Set<string>()

export function isNativeBridgeRejectEvent(event: any): event is NativeBridgeRejectEvent {
  return Object.values(NativeBridgeRejectEvent).includes(event)
}

export function sendMessage<T extends keyof NativeRequests>(
  type: T,
  data?: NativeRequests[T]['Request'],
  { skipResponse = false }: MessengerExtraConfiguration = {}
): Promise<NativeRequests[T]['Response']> {
  const id = uuid()
  const payload: NativeRequestPayload = { id, type, data }

  const sent = postToNative(payload)

  if (!sent) {
    postToNative({
      id: uuid(),
      type: NativeMessageType.ERROR,
      data: { message: NO_NATIVE_PLATFORM_FOUND_MESSAGE }
    })
    return Promise.reject(NativeBridgeRejectEvent.NO_NATIVE_PLATFORM_FOUND)
  }

  if (skipResponse) {
    return Promise.resolve(undefined)
  }

  return new Promise((resolve, reject) => {
    nativeAppResponseHandlers.set(id, {
      resolve,
      reject,
      payload,
      retry: NATIVE_TIMEOUT_RETRY
    })
    registerTimeout({ id, reject, payload })
  })
}

function registerTimeout({
  id,
  reject,
  payload
}: {
  id: string
  reject: (err?: any) => void
  payload: NativeRequestPayload
}) {
  setTimeout(() => {
    const handler = nativeAppResponseHandlers.get(id)
    if (!handler || handler === NATIVE_REQUEST_COMPLETE || handler === NATIVE_TIMEOUT) {
      return
    }

    if (handler.retry === 0) {
      nativeAppResponseHandlers.set(id, NATIVE_TIMEOUT)
      reject(NativeBridgeRejectEvent.TIMEOUT)
      return
    }

    nativeAppResponseHandlers.set(id, {
      ...handler,
      retry: handler.retry - 1
    })
    postToNative(handler.payload)
    registerTimeout({ id, reject, payload })
  }, NATIVE_TIMEOUT_MILLISECONDS)
}

function notifyErrorOnce(id: string, message: string) {
  if (errorEvents.has(id)) return

  errorEvents.add(id)

  postToNative({
    id: uuid(),
    type: NativeMessageType.ERROR,
    data: { message }
  })
}

function handleMissingHandler(id: string, type: string) {
  notifyErrorOnce(id, `No handler found for message ID: ${id} message type: ${type}`)
}

function handleTimeout(id: string, type: string) {
  notifyErrorOnce(id, `Native response for a message that has already timeout ID: ${id} message type: ${type}`)
}

exposeToWindow({
  handleNativeAppResponse: ({ id, data, type }: NativeResponsePayload): void => {
    const response = nativeAppResponseHandlers.get(id)

    if (!response) {
      return handleMissingHandler(id, type)
    }

    if (response === NATIVE_TIMEOUT) {
      return handleTimeout(id, type)
    }

    if (response !== NATIVE_REQUEST_COMPLETE) {
      response.resolve(data)
      nativeAppResponseHandlers.set(id, NATIVE_REQUEST_COMPLETE)
    }
  }
})
